/**
 * Session carton fill: pre-QC remaining counts down on every recorded inspection;
 * post-QC filled counts up only on a pass.
 */

export type ClampQcCartonTrackerSnapshot = {
  preQcRemaining: number;
  postQcFilled: number;
  postQcJustFilled: boolean;
};

export function applyClampQcInspectionToCartons(input: {
  preQcRemaining: number;
  postQcFilled: number;
  boxCapacity: number;
  passed: boolean;
}): ClampQcCartonTrackerSnapshot {
  const preQcRemaining = Math.max(0, input.preQcRemaining - 1);
  if (!input.passed) {
    return {
      preQcRemaining,
      postQcFilled: input.postQcFilled,
      postQcJustFilled: false,
    };
  }
  const postQcFilled = input.postQcFilled + 1;
  const postQcJustFilled =
    input.boxCapacity > 0 && input.postQcFilled < input.boxCapacity && postQcFilled >= input.boxCapacity;
  return {
    preQcRemaining,
    postQcFilled,
    postQcJustFilled,
  };
}

export function clampQcInventoryLabelPrintQuantity(input: {
  postQcFilled: number;
  lastPrintedQuantity: number;
  boxCapacity: number;
}): number {
  if (input.postQcFilled > 0) {
    return input.postQcFilled;
  }
  if (input.lastPrintedQuantity > 0) {
    return input.lastPrintedQuantity;
  }
  return Math.max(0, input.boxCapacity);
}

export function clampQcInventoryLabelPrintDisabled(selectedPrinter: string): boolean {
  return selectedPrinter.trim() === "";
}

export function parseClampQcCartonCountDraft(raw: string, fallback: number): number {
  const trimmed = raw.trim();
  const parsed = trimmed === "" ? 0 : Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 0 || !Number.isSafeInteger(parsed)) {
    return fallback;
  }
  return parsed;
}

export type ClampQcCartonOpenSnapshot = {
  preQcRemaining: number;
  postQcFilled: number;
  startedNextPreQcCarton: boolean;
  startedNextPostQcCarton: boolean;
};

/**
 * A serial already recorded this session or in the database does not start a new box.
 * A new serial on an empty pre-QC carton or a full post-QC carton starts the next box.
 */
export function clampQcCartonsForSerialInspection(input: {
  preQcRemaining: number;
  postQcFilled: number;
  boxCapacity: number;
  serialAlreadyRecorded: boolean;
}): ClampQcCartonOpenSnapshot {
  if (input.serialAlreadyRecorded || input.boxCapacity <= 0) {
    return {
      preQcRemaining: input.preQcRemaining,
      postQcFilled: input.postQcFilled,
      startedNextPreQcCarton: false,
      startedNextPostQcCarton: false,
    };
  }
  const startedNextPreQcCarton = input.preQcRemaining <= 0;
  const startedNextPostQcCarton = input.postQcFilled >= input.boxCapacity;
  return {
    preQcRemaining: startedNextPreQcCarton ? input.boxCapacity : input.preQcRemaining,
    postQcFilled: startedNextPostQcCarton ? 0 : input.postQcFilled,
    startedNextPreQcCarton,
    startedNextPostQcCarton,
  };
}

export function clampQcShouldApplyInspectionToCartonCounts(input: {
  sessionRescan: boolean;
  existingSavedInspection: boolean;
}): boolean {
  return !input.sessionRescan && !input.existingSavedInspection;
}

/** Adjust post-QC fill when an in-session rescan changes pass/fail. Pre-QC is unchanged. */
export function applyClampQcRescanToCartons(input: {
  postQcFilled: number;
  boxCapacity: number;
  priorPassed: boolean;
  newPassed: boolean;
}): { postQcFilled: number; postQcJustFilled: boolean } {
  if (input.priorPassed === input.newPassed) {
    return { postQcFilled: input.postQcFilled, postQcJustFilled: false };
  }
  if (input.priorPassed && !input.newPassed) {
    return {
      postQcFilled: Math.max(0, input.postQcFilled - 1),
      postQcJustFilled: false,
    };
  }
  const postQcFilled = input.postQcFilled + 1;
  const postQcJustFilled =
    input.boxCapacity > 0 &&
    input.postQcFilled < input.boxCapacity &&
    postQcFilled >= input.boxCapacity;
  return { postQcFilled, postQcJustFilled };
}
